import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Button } from "@/components/ui/button"
import { RefreshCw } from "lucide-react"
import { useCertNumbers } from '@/hooks/useCertNumbers'
import { NumberGrid } from './NumberGrid'
import { CertLookup } from './CertLookup'

interface NumberDisplayProps {
  className?: string
}

export const NumberDisplay = ({ className }: NumberDisplayProps) => {
  const [displayMode, setDisplayMode] = useState<'custom' | 'recent'>('custom')
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [selectedNumber, setSelectedNumber] = useState<string | null>(null)
  const { customNumbers, recentNumbers, refetch } = useCertNumbers()

  const handleRefresh = async () => {
    setIsRefreshing(true)
    console.log('Refreshing numbers...')
    await refetch()
    setTimeout(() => setIsRefreshing(false), 1000)
  }

  const handleNumberClick = (number: string) => {
    console.log('Number clicked:', number)
    setSelectedNumber(number)
  }

  return (
    <div className={`space-y-8 ${className}`}>
      <div className="flex justify-between items-center">
        <div className="flex gap-4">
          <button
            onClick={() => setDisplayMode('custom')}
            className={`px-6 py-2 rounded-full transition-all ${
              displayMode === 'custom'
                ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white'
                : 'bg-white/10 text-gray-300 hover:bg-white/20'
            }`}
          >
            靓号定制
          </button>
          <button
            onClick={() => setDisplayMode('recent')}
            className={`px-6 py-2 rounded-full transition-all ${
              displayMode === 'recent'
                ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white'
                : 'bg-white/10 text-gray-300 hover:bg-white/20'
            }`}
          >
            最近申请
          </button>
        </div>
        {displayMode === 'custom' && (
          <motion.div
            animate={{ rotate: isRefreshing ? 360 : 0 }}
            transition={{ duration: 1, ease: "easeInOut" }}
          >
            <Button
              onClick={handleRefresh}
              variant="outline"
              size="icon"
              className="w-10 h-10 rounded-full bg-white/10 text-gray-300 hover:bg-white/20 transition-all disabled:opacity-50"
              disabled={isRefreshing}
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </motion.div>
        )}
      </div>

      <AnimatePresence mode="wait">
        {displayMode === 'custom' ? (
          <motion.div
            key="custom"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <Tabs defaultValue="three" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="three">三位靓号</TabsTrigger>
                <TabsTrigger value="six">六位靓号</TabsTrigger>
                <TabsTrigger value="eight">八位靓号</TabsTrigger>
              </TabsList>
              {customNumbers && (
                <>
                  <TabsContent value="three" className="mt-4">
                    <NumberGrid numbers={customNumbers.threeDigit} />
                  </TabsContent>
                  <TabsContent value="six" className="mt-4">
                    <NumberGrid numbers={customNumbers.sixDigit} />
                  </TabsContent>
                  <TabsContent value="eight" className="mt-4">
                    <NumberGrid numbers={customNumbers.eightDigit} />
                  </TabsContent>
                </>
              )}
            </Tabs>
          </motion.div>
        ) : (
          <motion.div
            key="recent"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-8"
          >
            <NumberGrid 
              numbers={recentNumbers || []} 
              onNumberClick={handleNumberClick}
            />
            {selectedNumber && (
              <div className="mt-8">
                <CertLookup initialSearchTerm={selectedNumber} />
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}